import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type { Product, ProductVariant, CartItem } from '../types';

const CART_KEY = 'souad_cart';
const CART_EXPIRY_MINUTES = 30;

function loadCart(): CartItem[] {
  try {
    const stored = localStorage.getItem(CART_KEY);
    if (!stored) return [];
    const data = JSON.parse(stored);
    // Handle both old format (array) and new format ({items, timestamp})
    const items = Array.isArray(data) ? data : (data.items || []);
    const savedAt = Array.isArray(data) ? null : (data.savedAt || null);
    
    // Filter items older than 30 min
    if (savedAt) {
      const age = (Date.now() - savedAt) / 60000;
      if (age > CART_EXPIRY_MINUTES) {
        localStorage.removeItem(CART_KEY);
        return [];
      }
    }
    return items;
  } catch { return []; }
}

function saveCart(items: CartItem[]) {
  if (items.length === 0) {
    localStorage.removeItem(CART_KEY);
  } else {
    localStorage.setItem(CART_KEY, JSON.stringify({ items, savedAt: Date.now() }));
  }
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number; variant?: ProductVariant } }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_DRAWER' }
  | { type: 'OPEN_DRAWER' }
  | { type: 'CLOSE_DRAWER' };

function cartReducer(state: CartState, action: CartAction): CartState {
  let newState: CartState;
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity, variant } = action.payload;
      const maxQty = product.stockQuantity || 0;
      // Merge by product ID only (ignore variant/images to prevent duplicates)
      const existingIndex = state.items.findIndex(
        item => item.product.id === product.id
      );
      if (existingIndex >= 0) {
        const newItems = [...state.items];
        const currentQty = newItems[existingIndex].quantity;
        const newQty = Math.min(currentQty + quantity, maxQty);
        newItems[existingIndex] = { ...newItems[existingIndex], quantity: newQty };
        newState = { ...state, items: newItems, isOpen: true };
      } else {
        const cappedQty = Math.min(quantity, maxQty);
        if (cappedQty <= 0) return state;
        newState = { ...state, items: [...state.items, { product, quantity: cappedQty, variant }], isOpen: true };
      }
      break;
    }
    case 'REMOVE_ITEM':
      newState = { ...state, items: state.items.filter(item => item.product.id !== action.payload) };
      break;
    case 'UPDATE_QUANTITY': {
      const { productId, quantity } = action.payload;
      if (quantity <= 0) {
        newState = { ...state, items: state.items.filter(item => item.product.id !== productId) };
      } else {
        newState = { ...state, items: state.items.map(item => 
          item.product.id === productId 
            ? { ...item, quantity: Math.min(quantity, item.product.stockQuantity || 0) } 
            : item
        )};
      }
      break;
    }
    case 'CLEAR_CART':
      newState = { ...state, items: [] };
      break;
    case 'TOGGLE_DRAWER':
      newState = { ...state, isOpen: !state.isOpen };
      break;
    case 'OPEN_DRAWER':
      newState = { ...state, isOpen: true };
      break;
    case 'CLOSE_DRAWER':
      newState = { ...state, isOpen: false };
      break;
    default:
      return state;
  }
  saveCart(newState.items);
  return newState;
}

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  addToCart: (product: Product, quantity?: number, variant?: ProductVariant) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  total: number;
  count: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: loadCart(), isOpen: false });

  const addToCart = useCallback((product: Product, quantity = 1, variant?: ProductVariant) => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity, variant } });
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const toggleDrawer = useCallback(() => dispatch({ type: 'TOGGLE_DRAWER' }), []);
  const openDrawer = useCallback(() => dispatch({ type: 'OPEN_DRAWER' }), []);
  const closeDrawer = useCallback(() => dispatch({ type: 'CLOSE_DRAWER' }), []);

  const total = state.items.reduce((sum, item) => sum + Number(item.variant?.price ?? item.product.price) * item.quantity, 0);
  const count = state.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items: state.items, isOpen: state.isOpen, addToCart, removeFromCart, updateQuantity, clearCart, toggleDrawer, openDrawer, closeDrawer, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
}
